import { motion, type Variants } from 'motion/react';
import type { IframePoolOrderItem } from '../api';
import { FmvOnly } from './FmvOnly';
import { RevealStage } from './RevealStage';
import { DemoPrimaryAction, DemoSecondaryAction } from './DemoActions';
import { useDemoPullForRealPrimary } from './useDemoPullForRealPrimary';

const PULL_FOR_REAL_LABEL = 'Pull for Real';

export interface DemoBottomPanelProps {
  item: IframePoolOrderItem;
  /** Forward CTA label ("Try Again") — owned by the demo source. */
  nextPackCta: string;
  onTryAgain: () => void;
  /** Leaves the demo for the real pack page, where the pack can be bought. */
  onPullForReal: () => void;
  /** Reveal-cascade visibility flags — the same beats the real panel rides. */
  visibleFmv: boolean;
  visibleCta: boolean;
  visibleSecondary: boolean;
  fmvRoll: boolean;
  instant: boolean;
}

// Mirrors RevealBottomPanel's offer phase: the rows fan out bottom-up when
// the screen leaves for the next rip.
const container: Variants = {
  exit: { transition: { staggerChildren: 0.12, staggerDirection: -1 } },
};
const child: Variants = {
  exit: {
    y: 60,
    opacity: 0,
    transition: { duration: 0.34, ease: [0.4, 0, 1, 1] },
  },
};

/**
 * Bottom of the free demo rip's winner screen. Same anatomy as the real
 * one (RevealBottomPanel's offer phase) — Fair Market Value, a full-width
 * primary CTA, then a quiet secondary link where "Redeem This Item" sits —
 * so the demo reads exactly like the game it is demonstrating. Only the
 * actions differ: nothing is sold here, the primary pulls another free card
 * and the secondary leaves for the real pack — or the other way round when
 * the operator's `DemoReveal.primary` knob says `pull_for_real`.
 */
export function DemoBottomPanel({
  item,
  nextPackCta,
  onTryAgain,
  onPullForReal,
  visibleFmv,
  visibleCta,
  visibleSecondary,
  fmvRoll,
  instant,
}: DemoBottomPanelProps) {
  const hasFmv =
    item.fairMarketValue != null &&
    Number.isFinite(item.fairMarketValue) &&
    item.fairMarketValue > 0;

  const tryAgain = { label: nextPackCta, onClick: onTryAgain };
  const pullForReal = { label: PULL_FOR_REAL_LABEL, onClick: onPullForReal };
  const isPullForRealPrimary = useDemoPullForRealPrimary();
  const primary = isPullForRealPrimary ? pullForReal : tryAgain;
  const secondary = isPullForRealPrimary ? tryAgain : pullForReal;

  return (
    <div className="w-full max-w-[480px] mx-auto shrink-0">
      <motion.div
        variants={container}
        exit="exit"
        className="flex flex-col items-center gap-3 w-full"
      >
        {hasFmv && (
          <motion.div variants={child}>
            <RevealStage visible={visibleFmv} instant={instant}>
              <FmvOnly
                fmv={item.fairMarketValue!}
                roll={fmvRoll}
                instant={instant}
              />
            </RevealStage>
          </motion.div>
        )}

        <motion.div variants={child} className="w-full">
          <RevealStage visible={visibleCta} instant={instant} className="w-full">
            <DemoPrimaryAction {...primary} />
          </RevealStage>
        </motion.div>

        <motion.div variants={child} className="self-center">
          <RevealStage
            visible={visibleSecondary}
            instant={instant}
            className="self-center"
          >
            <DemoSecondaryAction {...secondary} />
          </RevealStage>
        </motion.div>
      </motion.div>
    </div>
  );
}
